/**
 * SurveyModal — the member-facing survey form.
 *
 * Presentation: a CENTRED Dialog. Like `ui/sheet.tsx`, `ui/dialog.tsx` is
 * Radix-backed, so this keeps the click-bubbling protection that hand-rolled
 * fixed overlays lack in this app (a portaled child dialog bubbles to the
 * backdrop through the React tree).
 *
 * KEYBOARD — the one thing centring costs us. `index.html` sets the viewport
 * WITHOUT `interactive-widget=resizes-content`, so the layout viewport does not
 * shrink when the software keyboard opens. A dead-centre modal therefore cannot
 * "float above" the keyboard the way the previous bottom sheet did. Three
 * properties keep the form usable anyway, and all three are load-bearing:
 *
 *   1. `max-h-[85dvh]` — dvh, never vh. vh is frozen at the tallest viewport on
 *      mobile Safari, so a vh cap lets the modal exceed the visible area.
 *   2. `flex-col` + `min-h-0` on the scroller — the BODY scrolls, so the footer
 *      stays attached to the modal instead of being pushed out of reach.
 *   3. Footer inside the modal, not the viewport — so it travels with it.
 *
 * On focus, mobile browsers scroll the focused field into the visual viewport,
 * so the input the member is typing into is always visible; Submit is reached by
 * dismissing the keyboard, which is the platform-normal gesture.
 *
 * ANONYMITY is one-way, and the signpost is the contract.
 *
 * `survey.isAnonymous` is the admin's setting. When it is false the member gets
 * a tickbox and may opt IN; there is no control to opt OUT, because the server
 * computes `is_anonymous OR p_anonymous` and would ignore one. So the effective
 * state is derivable here and `AnonymitySignpost` is the single component that
 * derives it — the tickbox and the footer line cannot disagree about what is
 * about to happen, because they read the same value.
 *
 * TWO EXITS, TWO MEANINGS.
 *
 *   ✕ / Esc / backdrop → dismiss. "Not this, ever." Stamps dismissed_at and the
 *     survey does not come back inside its rest period.
 *   Ask me later       → snooze. "Not now." Stamps snoozed_until = now() +
 *     the admin's snooze_duration, and the same invite is re-served after it.
 *
 * They were one button before. Collapsing them loses the distinction the member
 * is actually expressing, and the modal is not blocking, so closing it must
 * cost nothing.
 */
import { useState } from 'react';
import { Eye, EyeOff, ExternalLink, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ScaleInput } from './ScaleInput';
import {
  type ActiveSurvey,
  type SurveyAnswers,
  type SurveyQuestion,
} from '@/api/surveys';

export interface SurveyModalProps {
  survey: ActiveSurvey;
  open: boolean;
  submitting?: boolean;
  error?: string | null;
  onSubmit: (answers: SurveyAnswers, anonymous: boolean) => void;
  /** ✕, Esc or backdrop — "not this, ever". */
  onDismiss: () => void;
  /** "Ask me later" — "not now". */
  onSnooze: () => void;
}

function isAnswered(q: SurveyQuestion, answers: SurveyAnswers): boolean {
  const v = answers[q.id];
  if (q.type === 'scale') return typeof v === 'number';
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * The one place the effective anonymity state is turned into words.
 *
 * Both the footer line and the tickbox label read this, so a member can never
 * be told two different things about the same submission.
 */
function AnonymitySignpost({ anonymous }: { anonymous: boolean }) {
  const Icon = anonymous ? EyeOff : Eye;
  return (
    <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
      {anonymous ? 'Your answers are anonymous.' : 'Your answers are linked to your account.'}
    </p>
  );
}

export function SurveyModal({
  survey,
  open,
  submitting = false,
  error = null,
  onSubmit,
  onDismiss,
  onSnooze,
}: SurveyModalProps) {
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [anonymousChoice, setAnonymousChoice] = useState(false);

  // The admin's setting wins. When it is on, the tickbox is not rendered at all
  // — offering a control that cannot change the outcome is worse than none.
  const effectiveAnonymous = survey.isAnonymous || anonymousChoice;

  const setAnswer = (id: string, value: number | string) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  // Only non-optional questions block submission. A survey of entirely optional
  // questions is still submittable — that is deliberate, it lets someone send a
  // comment-only response.
  const missing = survey.questions.filter((q) => !q.optional && !isAnswered(q, answers));
  const canSubmit = missing.length === 0 && !submitting;

  const scaleCount = survey.questions.filter((q) => q.type === 'scale').length;

  // Optional per-survey link. Nullable by design — most surveys have none, and
  // an empty slot must render nothing rather than a stub. Built once and placed
  // at one end or the other so the two positions cannot drift apart.
  const formLink = survey.footerLinkUrl ? (
    <a
      href={survey.footerLinkUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-primary hover:underline"
    >
      {survey.footerLinkLabel || 'Leave us a review'}
      <ExternalLink aria-hidden className="h-3.5 w-3.5" />
    </a>
  ) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) onDismiss();
      }}
    >
      <DialogContent
        // Overrides DialogContent's own `grid gap-4 p-6`: this form owns its
        // spacing so the body can be the only scrolling region.
        // w-[calc(100%-2rem)] keeps a gutter on narrow phones — a full-bleed
        // centred card reads as a broken sheet.
        className="flex max-h-[85dvh] w-[calc(100%-2rem)] max-w-lg flex-col gap-0 rounded-2xl p-0"
      >
        {/* Header. pr-10 clears DialogContent's built-in close button, which is
            absolutely positioned at right-4 top-4 and would otherwise sit on
            top of a long title. */}
        <div className="px-5 pt-5 pb-3 pr-10">
          {/* Card Title per the §K scale: sans 18/700. Sans is correct here —
              ALFIE showing, not speaking. DialogTitle (not a bare h2) so Radix
              can wire aria-labelledby — without it Radix logs an a11y warning
              and screen readers announce the dialog unlabelled. */}
          <DialogTitle className="text-lg font-bold text-foreground">
            {survey.title}
          </DialogTitle>
          {survey.subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{survey.subtitle}</p>
          )}
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 pb-4">
          {survey.footerLinkPosition === 'top' && formLink}

          {survey.questions.map((q, i) => (
            <div key={q.id}>
              <label
                htmlFor={`survey-q-${q.id}`}
                className="mb-2 block text-sm font-medium text-foreground"
              >
                {i + 1}. {q.label}
                {q.optional && (
                  <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                    (optional)
                  </span>
                )}
              </label>

              {q.type === 'scale' ? (
                <ScaleInput
                  ariaLabel={q.label}
                  scaleMax={q.scaleMax}
                  lowLabel={q.lowLabel}
                  highLabel={q.highLabel}
                  value={typeof answers[q.id] === 'number' ? (answers[q.id] as number) : null}
                  onChange={(v) => setAnswer(q.id, v)}
                />
              ) : q.type === 'choice' ? (
                // Buttons, not a 2-tile scale: a scale renders "1 2" and its
                // answer would be counted as warmth. min-h-[44px] is the Apple
                // HIG floor — this is a one-tap control on a phone, and the
                // whole point of the question is that it is instant.
                <div
                  role="group"
                  aria-label={q.label}
                  className="flex flex-wrap gap-2"
                >
                  {q.options.map((opt) => {
                    const selected = answers[q.id] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setAnswer(q.id, opt)}
                        aria-pressed={selected}
                        className={`min-h-[44px] min-w-[88px] rounded-full border px-5 text-base font-medium transition-colors sm:min-h-[36px] sm:text-sm ${
                          selected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card text-foreground hover:bg-foreground/[0.06]'
                        }`}
                      >
                        {opt === 'yes' ? 'Yes' : opt === 'no' ? 'No' : 'Maybe'}
                      </button>
                    );
                  })}
                </div>
              ) : (
                // text-base on mobile: iOS Safari auto-zooms the page when
                // focusing an input under 16px, and maximum-scale=1 does not
                // reliably suppress that in a standalone PWA.
                // field-sizing:content grows the box without JS.
                // max-h in px, never vh — iOS vh ignores the keyboard, so a vh cap
                // would push Submit underneath it.
                <textarea
                  id={`survey-q-${q.id}`}
                  rows={2}
                  value={typeof answers[q.id] === 'string' ? (answers[q.id] as string) : ''}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  placeholder={q.placeholder}
                  maxLength={2000}
                  className="w-full resize-none rounded-lg border border-border bg-card px-3 py-2 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring sm:text-sm [field-sizing:content] min-h-[44px] max-h-[140px] overflow-y-auto"
                />
              )}
            </div>
          ))}

          {/* Opt-in only, and only where it can change the outcome. min-h-[44px]
              on the label makes the whole row the tap target, not the 16px box. */}
          {!survey.isAnonymous && (
            <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-sm text-foreground">
              <input
                type="checkbox"
                checked={anonymousChoice}
                onChange={(e) => setAnonymousChoice(e.target.checked)}
                disabled={submitting}
                className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-1 focus:ring-ring"
              />
              Answer anonymously
            </label>
          )}

          {survey.footerLinkPosition !== 'top' && formLink}

          {error && <p className="text-xs text-danger">{error}</p>}
        </div>

        {/* Footer. Rounded to match the card's own corners now that it is the
            modal's bottom edge rather than the screen's. Wraps on very narrow
            phones so the 44px targets never squash below the tap minimum. */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-b-2xl border-t border-border px-5 py-3">
          <AnonymitySignpost anonymous={effectiveAnonymous} />
          <div className="flex items-center gap-2">
            {/* Snooze, not dismiss. ✕ / Esc / backdrop are the dismiss path. */}
            <button
              type="button"
              onClick={onSnooze}
              disabled={submitting}
              className="min-h-[44px] rounded-lg px-3 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Ask me later
            </button>
            <button
              type="button"
              onClick={() => onSubmit(answers, anonymousChoice)}
              disabled={!canSubmit}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit
            </button>
          </div>
        </div>

        {/* Screen-reader progress cue; also explains a disabled Submit. */}
        {missing.length > 0 && scaleCount > 0 && (
          <p aria-live="polite" className="sr-only">
            {missing.length} question{missing.length === 1 ? '' : 's'} still to answer.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SurveyModal;
